import { getProps } from 'utils/DOM';

type Decorator = (node: Element, props: Record<string, unknown>) => void;

interface Listener {
  selector: string;
  decorator: Decorator;
  settings: Record<string, unknown>;
}

/** A node a decorator has been attached to, see `registerDecorator`. */
type DecoratedNode = Element & { decorator?: { destroy: () => void } };

const listeners: Listener[] = [];

function registerDecorator(
  node: Element,
  decorate: Decorator,
  settings: Record<string, unknown>
) {
  const props = getProps(node);
  decorate(node, {
    ...settings,
    ...props,
  });
}

function checkAdded(node: Node) {
  const added = node as Element;
  if (['STYLE', '#text'].indexOf(added.nodeName) === -1) {
    listeners.forEach(({ selector, decorator, settings }) => {
      try {
        if (typeof added.matches == 'function') {
          if (added.matches(selector)) {
            registerDecorator(added, decorator, settings);
          } else {
            const children = added.querySelectorAll(selector);
            if (children.length) {
              Array.from(children).forEach(node => {
                registerDecorator(node, decorator, settings);
              });
            }
          }
        }
        /* eslint-disable no-empty */
      } catch (e) {}
    });
  }
}

function checkRemoved(node: Node) {
  const removed = node as DecoratedNode;
  if (['STYLE', '#text'].indexOf(removed.nodeName) === -1) {
    listeners.forEach(({ selector }) => {
      try {
        const children = removed.querySelectorAll(selector);

        Array.from(children as NodeListOf<DecoratedNode>).forEach(node => {
          if (node.decorator) {
            node.decorator.destroy();
          }
        });

        if (removed.decorator) {
          removed.decorator.destroy();
        }
        /* eslint-disable no-empty */
      } catch (e) {}
    });
  }
}

const observer = new MutationObserver(mutationsList => {
  for (const mutation of mutationsList) {
    if (mutation.type == 'childList') {
      if (mutation.removedNodes.length > 0) {
        mutation.removedNodes.forEach(checkRemoved);
      }

      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(checkAdded);
      }
    }
  }
});

observer.observe(document, { childList: true, subtree: true });

export default <N extends Element = Element>(
  selector: string,
  decorator: (node: N, props: Record<string, unknown>) => void,
  settings: Record<string, unknown> = {}
) => {
  // The decorator only sees nodes the selector matches, which are the N it asks for.
  const register = decorator as unknown as Decorator;

  listeners.push({
    selector,
    decorator: register,
    settings,
  });

  try {
    const nodes = document.querySelectorAll(selector);
    Array.prototype.forEach.call(nodes, node => {
      registerDecorator(node, register, settings);
    });
    /* eslint-disable no-empty */
  } catch (e) {}
};
